import {
  ConfigParametersRepositoryInstance,
  ConfigRepositoryParameters,
} from './types';
import { createTrackingStorageClient } from './storageClient/storage';
import { init as initDatamesh } from '../html-events/init';
import {
  handleDatameshInitError,
  handleGaGroupIdError,
  handleTrackingStorageError,
} from './error';
import { deepMerge } from '../utils/object';

let configParametersRepositoryInstance = null;

export const createConfigParametersRepository =
  (): ConfigParametersRepositoryInstance => {
    if (configParametersRepositoryInstance) {
      return configParametersRepositoryInstance;
    }

    let storedParams: Partial<ConfigRepositoryParameters> = {};
    let trackingStorageClient = null;

    const filterParams = (
      params: Partial<ConfigRepositoryParameters>
    ): Partial<ConfigRepositoryParameters> => {
      if (!params) return;
      const validKeys = [
        'generic',
        'datamesh',
        'googleAnalytics',
        'tagCommander',
      ];
      if (params.googleAnalytics && trackingStorageClient) {
        handleGaGroupIdError(params.googleAnalytics.ga4GroupId);
      }
      return Object.keys(params)
        .filter((key) => validKeys.includes(key))
        .reduce(
          (obj, key) => ({
            ...obj,
            [key]: params[key],
          }),
          {}
        );
    };

    const setParameters = (
      configParams: Partial<ConfigRepositoryParameters>
    ) => {
      const filteredParams = filterParams(configParams) ?? {};
      storedParams = deepMerge(storedParams, filteredParams);
    };

    const createInternalTrackingStorageClient = () => {
      const storageClient = storedParams?.generic?.storageClient;
      const canCreateTrackingStorageClient =
        handleTrackingStorageError(storageClient);
      if (!canCreateTrackingStorageClient) return;
      trackingStorageClient = createTrackingStorageClient(storageClient);
    };

    const initializeDatamesh = () => {
      const visitID = storedParams?.generic?.visitID;
      const context = storedParams?.datamesh?.context;
      const canInitializeDatamesh = handleDatameshInitError(visitID, context);
      if (!canInitializeDatamesh) return;
      initDatamesh({ visitID, context });
    };

    configParametersRepositoryInstance = {
      init: (configParams: Partial<ConfigRepositoryParameters>) => {
        setParameters(configParams);
        createInternalTrackingStorageClient();
        initializeDatamesh();
      },
      setParameters,
      getStoredParameters: () => storedParams,
      getGenericParameters: () => storedParams.generic,
      getDatameshParameters: () => storedParams.datamesh,
      getGoogleAnalyticsParameters: () => storedParams.googleAnalytics,
      getTagCommanderParameters: () => storedParams.tagCommander,
      getTrackingStorageClient: () => trackingStorageClient,
    };

    return configParametersRepositoryInstance;
  };

export const resetConfigParametersRepository = () => {
  configParametersRepositoryInstance = null;
};
